import * as React from 'react';

import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import { answerSorts, validateForumTypeName } from 'bundles/discussions/constants';
import type { AnswerSort } from 'bundles/discussions/constants';
import { getAssignmentPostItemReplyLink } from 'bundles/discussions/utils/discussionsUrl';

import { extractForumPostId } from '../../../__helpers__/forumPostDataHelpers';
import type {
  AuthorProfile,
  Contributor,
  CreatorType,
  ForumAnswerLookupProps,
  ForumAnswersV1Response,
  ForumPost,
  ForumPostReply,
  ForumPostWithCreator,
  PaginationProps,
} from '../__types__';
import ForumAnswerModel from '../models/ForumAnswer';

export function toForumCardModel(props: ForumPostReply & CreatorType): ForumPostWithCreator {
  return {
    answerBadge: undefined,
    content: { question: undefined, details: props.content },
    definition: props.content.definition,
    dontNotify: props.dontNotify,
    typeName: props.content.typeName,
    courseId: props.courseId,
    createdAt: props.createdAt,
    creatorId: props.creatorId,
    forumCommentId: props.forumCommentId,
    editReason: props.editReason,
    followCount: undefined,
    forumId: undefined,
    forumQuestionId: props.forumQuestionId,
    id: props.id,
    questionId: extractForumPostId(props.forumQuestionId),
    answerId: props.forumAnswerId,
    hide: props.hide,
    isFlagged: props.isFlagged,
    isFollowing: undefined,
    isUpvoted: props.isUpvoted,
    lastAnsweredAt: undefined,
    lastAnsweredBy: undefined,
    sessionId: undefined,
    showEditor: props.showEditor,
    state: props.state,
    topLevelAnswerCount: props.childAnswerCount,
    totalAnswerCount: props.childAnswerCount,
    upvoteCount: props.upvoteCount,
    userId: props.userId,
    viewCount: undefined,
    creator: props.creator,
    type: props.type,
    topLevelForumAnswerId: props.topLevelForumAnswerId,
    forumType: validateForumTypeName(props.forumType) ? props.forumType : undefined,
  };
}

type ForumAnswersDataProviderResponse = {
  contributors?: Contributor[];
  replies?: ForumPostWithCreator[];
};
type Context = { forumId: string; courseId: string; questionId: string };

function extractCreator(response: ForumAnswersV1Response, creatorId: number): AuthorProfile | undefined {
  if (!response.ForumAnswersV1.linked || !response.ForumAnswersV1.linked.onDemandSocialProfilesV1) {
    return undefined;
  }
  return response.ForumAnswersV1.linked?.onDemandSocialProfilesV1?.find(
    (contributor: AuthorProfile) => contributor && contributor.userId === creatorId
  );
}

function extractReplies(response: ForumAnswersV1Response, context?: Context) {
  const replies = response.ForumAnswersV1?.elements;
  if (replies && Array.isArray(replies)) {
    return replies.map((forumReply) => {
      const forumPost = toForumCardModel(forumReply);
      const creatorId = forumPost.creatorId;
      let deepLink = '';

      if (context) {
        deepLink = getAssignmentPostItemReplyLink(context.forumId, context.questionId, forumReply.forumAnswerId);
      }

      return {
        ...forumPost,
        creator: extractCreator(response, creatorId),
        deepLink,
      };
    });
  }
  return [] as ForumPost[];
}

export type ThreadDetailsStoreResponseProps = {
  isQuestionLoaded: boolean;
  loadingState: string;
  page: number;
  pageCount: number;
  questionId: string;
  replies: (ForumPostReply & CreatorType)[];
  savingStates: any;
  sort: AnswerSort;
};

class ForumAnswersProvider extends React.Component<ForumAnswerLookupProps & PaginationProps> {
  static contextTypes = {
    forumId: PropTypes.string,
    courseId: PropTypes.string,
    questionId: PropTypes.string,
  };

  declare context: LegacyContextType<typeof ForumAnswersProvider.contextTypes>;

  render() {
    const { forumQuestionId, children, page, limit, sortOrder } = this.props;
    const defaultSortOrder = answerSorts.newestSort;

    const questionId = extractForumPostId(forumQuestionId);

    return (
      // @ts-expect-error ts-migrate(2322) FIXME: Type 'string' is not assignable to type 'AnswerSor... Remove this comment to see the full error message
      <ForumAnswerModel.Query id={questionId} page={page} limit={limit} sortOrder={sortOrder || defaultSortOrder}>
        {(props) => {
          if (props.replies) {
            const parsedReplies = props?.replies?.map(toForumCardModel) || [];
            return children({ loading: false, error: undefined, data: { ...props, replies: parsedReplies } });
          } else {
            return children({ loading: true });
          }
        }}
      </ForumAnswerModel.Query>
    );
  }
}

export const ForumAnswerByIdProvider: React.FC<ForumAnswerLookupProps & PaginationProps> = ({
  forumQuestionId,
  children,
  page,
  limit,
}) => {
  return (
    <ForumAnswerModel.Query id={forumQuestionId} page={page} limit={limit} sortOrder={answerSorts.newestSort}>
      {(props) => {
        if (props.replies) {
          const parsedReplies = props?.replies?.map(toForumCardModel) || [];
          return children({ loading: false, error: undefined, data: { ...props, replies: parsedReplies } });
        } else {
          return children({ loading: true });
        }
      }}
    </ForumAnswerModel.Query>
  );
};
export default ForumAnswersProvider;
